import { useCallback, useContext } from 'react';
import Cookies from 'universal-cookie';

import appConfig from '@nerdwallet/app-config';

import { getLogger } from '@lib/logging';
import { useAnalyticsContext } from './useAnalyticsContext';
import { SegmentPageViewContext } from './components/segment-page-view';

const segmentLogger = getLogger('SegmentLogger');

const commonProps = {
  brand: 'Nerdwallet',
  // eslint-disable-next-line no-underscore-dangle
  deployable_name: appConfig?._meta?.deployable || 'my-nerdwallet',
  // eslint-disable-next-line no-underscore-dangle
  deployable_version: appConfig?._meta?.version || '0.0.0',
  // We are setting this flag as false for the default value
  meaningful_engagement_flag: false,
};

type SegmentTrackFunction = (
  eventName: string,
  eventProps?: Record<string, any>,
) => Promise<void>;
type SegmentLinkFunction = (
  element: HTMLElement,
  eventName: string,
  eventProps?: Record<string, any>,
) => Promise<void>;

export const useAnalytics = (): {
  segmentTrack: SegmentTrackFunction;
  segmentTrackLink: SegmentLinkFunction;
  segmentPage: SegmentTrackFunction;
} => {
  const {
    analytics,
    pageViewId,
    oldPageViewId,
    generateNewPageViewId,
    updatePageName,
    updatePageViewId,
  } = useAnalyticsContext();

  // If a segmentPageViewContext exists, use its page-view-id in place of the global analytics-context
  const segmentPageViewContext = useContext(SegmentPageViewContext);

  const segmentTrack = useCallback(
    async (eventName: string, eventProps: Record<string, any> = {}) => {
      // If a segmentPageViewContext exists, use its pageview-id in place of the global analytics-context's
      let parentPageViewId = pageViewId;
      let parentPreviousPageViewId = oldPageViewId;
      if (segmentPageViewContext) {
        parentPageViewId = segmentPageViewContext?.pageViewId;
        parentPreviousPageViewId = segmentPageViewContext?.previousPageViewId;
      }
      await analytics?.track(eventName, {
        impression_pageview_id: parentPageViewId,
        impression_referrer_pageview_id: parentPreviousPageViewId,
        /* Segment url defaults to the canonical URL. Override it to use the actual url. */
        url: window.location.href,
        ...commonProps,
        ...eventProps,
      });
    },
    [analytics, oldPageViewId, pageViewId, segmentPageViewContext],
  );

  const segmentTrackLink = useCallback(
    async (
      element: HTMLElement,
      eventName: string,
      eventProps: Record<string, any> = {},
    ) => {
      await analytics?.trackLink(element, eventName, {
        action_location_url: window.location.href,
        ...commonProps,
        ...eventProps,
      });
    },
    [analytics],
  );

  const segmentPage = useCallback(
    async (pageName: string, eventProps: Record<string, any> = {}) => {
      const cookies = new Cookies();
      updatePageName(pageName);

      // If pageViewId was passed as a prop, use it to update the analytics-context, otherwise generate a fresh id.
      const { newPageViewId, previousPageViewId } =
        eventProps && eventProps?.pageViewId
          ? updatePageViewId(eventProps?.pageViewId)
          : generateNewPageViewId();

      await analytics?.page(pageName, {
        pageview_id: newPageViewId,
        referrer_pageview_id: previousPageViewId,
        traditional_device_id: cookies.get('cookie_id'),
        /* Segment url defaults to the canonical URL. Override it to use the actual url. */
        url: window.location.href,
        ...commonProps,
        ...eventProps,
      });
    },
    [analytics, generateNewPageViewId, updatePageName, updatePageViewId],
  );

  const segmentTrackInstance = useCallback(
    (eventName: string, eventProps: Record<string, any> = {}) => {
      return segmentTrack(eventName, eventProps).catch((err) => {
        segmentLogger.error(err);
      });
    },
    [segmentTrack],
  );

  const segmentTrackLinkInstance = useCallback(
    (
      element: HTMLElement,
      eventName: string,
      eventProps: Record<string, any> = {},
    ) => {
      return segmentTrackLink(element, eventName, eventProps).catch((err) => {
        segmentLogger.error(err);
      });
    },
    [segmentTrackLink],
  );

  const segmentPageInstance = useCallback(
    (pageName: string, eventProps: Record<string, any> = {}) => {
      return segmentPage(pageName, eventProps).catch((err) => {
        segmentLogger.error(err);
      });
    },
    [segmentPage],
  );

  return {
    segmentTrack: segmentTrackInstance,
    segmentTrackLink: segmentTrackLinkInstance,
    segmentPage: segmentPageInstance,
  };
};
